import { Loader } from '@uswitch/comms-ui.loader'
import { Button } from '@uswitch/spark'
import React, { Fragment, useEffect, useState } from 'react'

import Deal from '~/components/deal'
import PriceRiseBanner from '~/components/filter-banner/price-rise-banner'
import Tenancy from '~/components/tenancy'
import EcommerceTracking from '~/components/tracking/ecommerce'
import { EcommerceProvider } from '~/contexts/ecommerce-context'
import { usePageContext } from '~/contexts/page-context'
import { insertTenanciesIntoDealsList } from '~/utils/deal-list'
import { notEmpty } from '~/utils/empty'
import { pluralize } from '~/utils/string'

import * as st from './styles'

const MIN_LOAD_TIME = 1000

const Table = ({
  hasNextPage,
  address,
  postcode,
  loading,
  inputVariables,
  sortBy,
  ecommerceData,
  tenancies,
  deals,
  comparisonId,
  paginate,
  onDealsChange,
  intent,
  currentProvider,
  dealsCount,
  setShow10MoreClicked,
  show10MoreClicked,
  criticalJs = false,
  isPostcodeInMs3Area,
  currentProviderId,
  setFilter = () => {},
  cityFibreDealsAvailable
}) => {
  const { pageId, listnames } = usePageContext()
  const [isLoadingMore, setLoadingMore] = useState(false)
  const [hasUspAnimated, setHasUspAnimated] = useState(false)

  const list = listnames().table

  useEffect(() => {
    onDealsChange && onDealsChange()
  }, [JSON.stringify(deals)])

  const onClick = () => {
    setShow10MoreClicked(true)
    setLoadingMore(true)
    paginate()
  }

  useEffect(() => {
    if (!loading && isLoadingMore) {
      setLoadingMore(false)
    }
  }, [loading, isLoadingMore])

  if (isPostcodeInMs3Area && !deals.length) {
    return <Fragment />
  }

  const renderDeal = (deal, idx, variant) => (
    <Deal
      key={`${deal.id}${idx}`}
      variant={variant}
      intent={intent}
      deal={deal}
      list={list}
      placementType={'table'}
      position={idx + 1}
      criticalJs={criticalJs}
      hasUspAnimated={hasUspAnimated}
      setHasUspAnimated={setHasUspAnimated}
      cityFibreDealsAvailable={cityFibreDealsAvailable}
    />
  )

  const renderTenancy = (tenancy, idx) => {
    const {
      display: { deal },
      position: { exposure, position }
    } = tenancy

    return (
      <Tenancy
        key={`${deal.id}tenancy${position}`}
        deal={deal}
        isSponsored
        intent={intent}
        tenancy={tenancy}
        list={listnames(exposure)[`p${position}`]}
        position={idx + 1}
        criticalJs={criticalJs}
        hasUspAnimated={hasUspAnimated}
        setHasUspAnimated={setHasUspAnimated}
        cityFibreDealsAvailable={cityFibreDealsAvailable}
      />
    )
  }

  const renderPriceRiseBanner = () => (
    <li sx={st.tablePriceRiseBanner} key='price-rise-banner'>
      <PriceRiseBanner setFilter={setFilter} currentProvider={currentProviderId} />
    </li>
  )

  const { allDeals, allDealsRendered: dealsList } = insertTenanciesIntoDealsList({
    deals,
    tenancies,
    renderDeal,
    renderTenancy,
    renderPriceRiseBanner
  })

  const EmptyTable = () => <div sx={st.generateEmptyTableStyles({ hasProviders: pageId.includes('providers') })} />

  const showMoreDealsCount = Math.min(10, dealsCount - deals.length)

  return (
    <Fragment>
      <div className='us-ct__container'>
        <Loader loading={loading} minimumLoadTime={MIN_LOAD_TIME} loadedData={inputVariables} customCSS={st.loader}>
          {notEmpty(deals) ? (
            <EcommerceProvider
              value={{
                address,
                comparisonId,
                currentProvider,
                deals: allDeals,
                dealsTotalCount: dealsCount,
                postcode,
                list,
                metadata: ecommerceData,
                show10MoreClicked,
                sort: sortBy,
                hasUspAnimated
              }}
            >
              <EcommerceTracking
                postcode={postcode}
                address={address}
                comparisonId={comparisonId}
                comparison={deals}
                sort={sortBy}
                list={list}
                metadata={ecommerceData}
                dealsCount={dealsCount}
                tenanciesCount={tenancies.length}
                show10MoreClicked={show10MoreClicked}
                hasUspAnimated={hasUspAnimated}
              >
                {dealsList}
              </EcommerceTracking>
            </EcommerceProvider>
          ) : (
            <EmptyTable />
          )}
        </Loader>
      </div>

      {hasNextPage && (
        <div
          sx={{
            display: 'flex',
            justifyContent: 'center',
            pt: [10, 10],
            pb: [32, 32, 64]
          }}
        >
          {/* TODO: this should trigger a submit to retain the jsless functionality. */}
          <Button
            variant='primary'
            name='visible_products'
            data-event
            data-event-category='Responsive Compare'
            data-event-action='Show 10 more products'
            data-event-label='Show 10 more products'
            data-event-value={deals.length}
            onClick={onClick}
            disabled={loading}
            style={{
              padding: '0.63em 1.25em',
              fontWeight: 700
            }}
          >
            Show {showMoreDealsCount} more {pluralize('deal', showMoreDealsCount)}
          </Button>
        </div>
      )}
    </Fragment>
  )
}

export default Table
